// soccer_admin/src/pages/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { 
  UsersIcon, CreditCardIcon, TrophyIcon, ChatBubbleLeftRightIcon,
  CalendarIcon, ChartBarIcon, CurrencyDollarIcon, StarIcon,
  GlobeAltIcon, FireIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon,
  ClockIcon, ExclamationTriangleIcon, CheckCircleIcon, UserGroupIcon,
  CpuChipIcon, BoltIcon, ShieldCheckIcon, FaceSmileIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
  AreaChart, Area, ComposedChart, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { format, formatDistanceToNow, isValid } from 'date-fns';

// ============================================================
// Helper: Safe date formatting
// ============================================================
const safeFormatDistanceToNow = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (!isValid(date)) return 'Invalid date';
  return formatDistanceToNow(date, { addSuffix: true });
};

const safeFormat = (dateString, formatStr) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (!isValid(date)) return 'Invalid date';
  return format(date, formatStr);
};

// ============================================================
// كروت الإحصائيات
// ============================================================
const StatsCard = ({ title, value, icon: Icon, color, subValue, subLabel, trend, trendLabel }) => (
  <div className="bg-gray-800 rounded-xl p-5 border border-gray-700 hover:border-yellow-500/30 transition-all duration-300 shadow-lg hover:shadow-yellow-500/10">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium">{title}</p>
        <p className="text-3xl font-bold text-white mt-1">{value}</p>
        {subValue !== undefined && (
          <p className="text-xs text-gray-500 mt-1">{subLabel}: <span className="text-gray-300">{subValue}</span></p>
        )}
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <ArrowTrendingUpIcon className="h-3 w-3" /> : <ArrowTrendingDownIcon className="h-3 w-3" />}
            <span>{Math.abs(trend)}% {trendLabel}</span>
          </div>
        )}
      </div>
      <div className={`p-3 rounded-full ${color} bg-opacity-20`}>
        <Icon className={`h-8 w-8 ${color}`} />
      </div>
    </div>
  </div>
);

// ============================================================
// كروت صغيرة
// ============================================================
const MiniStatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50 hover:border-gray-600 transition">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-full ${color} bg-opacity-20`}>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div>
        <p className="text-gray-400 text-xs">{title}</p>
        <p className="text-xl font-bold text-white">{value}</p>
        {subtitle && <p className="text-[10px] text-gray-500">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// ============================================================
// جدول المباريات الأخيرة
// ============================================================
const RecentMatchesTable = ({ matches }) => {
  if (!matches || matches.length === 0) {
    return <div className="text-gray-500 text-sm text-center py-6">No recent matches</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-700">
          <tr className="text-left text-gray-400 text-xs uppercase tracking-wider">
            <th className="pb-2 pr-4">#</th>
            <th className="pb-2 pr-4">Players</th>
            <th className="pb-2 pr-4">Score</th>
            <th className="pb-2 pr-4">Competition</th>
            <th className="pb-2 pr-4">Time</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {matches.slice(0, 10).map((match, idx) => {
            const matchDate = match.match_date || match.created_at;
            return (
              <tr key={match.id || idx} className="hover:bg-gray-700/30 transition">
                <td className="py-2 pr-4 text-gray-500">#{match.id || idx + 1}</td>
                <td className="py-2 pr-4">
                  <span className="text-white">{match.user1_username || match.user1}</span>
                  <span className="text-gray-600 mx-1">vs</span>
                  <span className="text-white">{match.user2_username || match.user2}</span>
                </td>
                <td className="py-2 pr-4 font-mono">
                  <span className="text-yellow-400">{match.user1_score ?? 0}</span>
                  <span className="text-gray-600 mx-1">-</span>
                  <span className="text-yellow-400">{match.user2_score ?? 0}</span>
                </td>
                <td className="py-2 pr-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    match.competition === 'domestic' ? 'bg-green-600/20 text-green-400' :
                    match.competition === 'champions' ? 'bg-yellow-600/20 text-yellow-400' :
                    match.competition === 'europa' ? 'bg-blue-600/20 text-blue-400' :
                    'bg-gray-600/20 text-gray-400'
                  }`}>
                    {match.competition || 'friendly'}
                  </span>
                </td>
                <td className="py-2 pr-4 text-gray-400 text-xs">
                  {matchDate ? safeFormatDistanceToNow(matchDate) : 'N/A'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================
// المكون الرئيسي
// ============================================================
const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // الإحصائيات الأساسية
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCards: 0,
    totalMatches: 0,
    totalSupportMessages: 0,
    activeUsersToday: 0,
    totalRevenue: 0,
    totalPremiumPurchases: 0,
    pendingSponsorships: 0,
    matchesToday: 0,
    newUsersToday: 0,
  });

  // بيانات المخططات
  const [matchTrend, setMatchTrend] = useState([]);
  const [cardRarityDistribution, setCardRarityDistribution] = useState([]);
  const [userContinentDistribution, setUserContinentDistribution] = useState([]);
  const [competitionDistribution, setCompetitionDistribution] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [topPlayers, setTopPlayers] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [seasonStats, setSeasonStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [premiumNameStats, setPremiumNameStats] = useState(null);

  const COLORS = ['#FBBF24', '#9CA3AF', '#CD7F32', '#60A5FA', '#F472B6', '#34D399', '#F59E0B', '#8B5CF6'];

  const fetchAllStats = async () => {
    setLoading(true);
    setError(null);
    try {
      // استدعاء جميع الإحصائيات بالتوازي
      const [
        usersRes, cardsRes, matchesRes, supportRes, trendRes, rarityRes,
        continentRes, compRes, scorersRes, topPlayersRes, recentMatchesRes,
        recentUsersRes, seasonRes, revenueRes, premiumStatsRes, activityRes
      ] = await Promise.all([
        API.get('/admin/stats/users'),
        API.get('/admin/stats/cards'),
        API.get('/admin/stats/matches'),
        API.get('/admin/stats/support'),
        API.get('/admin/stats/match-trend'),
        API.get('/admin/stats/card-rarity'),
        API.get('/admin/stats/user-continents'),
        API.get('/admin/stats/competition-distribution'),
        API.get('/admin/stats/top-scorers'),
        API.get('/admin/stats/top-players'),
        API.get('/admin/stats/recent-matches'),
        API.get('/admin/stats/recent-users'),
        API.get('/admin/stats/season-overview'),
        API.get('/admin/stats/revenue'),
        API.get('/admin/stats/premium-names'),
        API.get('/admin/stats/activity'),
      ]);

      setStats({
        totalUsers: usersRes.data.total || 0,
        totalCards: cardsRes.data.total || 0,
        totalMatches: matchesRes.data.total || 0,
        totalSupportMessages: supportRes.data.total || 0,
        activeUsersToday: activityRes.data.activeUsersToday || 0,
        totalRevenue: revenueRes.data.totalRevenue || 0,
        totalPremiumPurchases: premiumStatsRes.data.totalPurchases || 0,
        pendingSponsorships: revenueRes.data.pendingSponsorships || 0,
        matchesToday: activityRes.data.matchesToday || 0,
        newUsersToday: activityRes.data.newUsersToday || 0,
      });

      setMatchTrend(trendRes.data || []);
      setCardRarityDistribution(rarityRes.data || []);
      setUserContinentDistribution(continentRes.data || []);
      setCompetitionDistribution(compRes.data || []);
      setTopScorers(scorersRes.data || []);
      setTopPlayers(topPlayersRes.data || []);
      setRecentMatches(recentMatchesRes.data || []);
      setRecentUsers(recentUsersRes.data || []);
      setSeasonStats(seasonRes.data || null);
      setRevenueData(revenueRes.data.dailyRevenue || []);
      setPremiumNameStats(premiumStatsRes.data || null);

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllStats();
  }, []);

  const handleRefresh = () => {
    if (!loading) {
      fetchAllStats();
    }
  };

  // دالة للتعامل مع النقر على عناصر المخططات
  const handleChartClick = (data) => {
    if (data && data.payload) {
      const { name, value, payload } = data.payload;
      alert(`📊 ${name || payload?.name || 'Item'}: ${value || payload?.count || payload?.value || 0}`);
    } else if (data && data.activeLabel) {
      // للأخطاء
      alert(`📊 Date: ${data.activeLabel}, Count: ${data.activePayload?.[0]?.value || 0}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-yellow-400 border-solid mx-auto mb-4"></div>
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center text-red-400">
        <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-3" />
        <p>{error}</p>
        <button 
          onClick={handleRefresh}
          className="mt-4 bg-red-500/20 hover:bg-red-500/30 px-4 py-2 rounded-lg text-sm transition"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Refresh button */}
      <div className="flex flex-wrap justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <ChartBarIcon className="h-8 w-8 text-yellow-400" />
            Dashboard
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Real-time overview of Soccer Up platform
            <span className="ml-2 text-gray-500 text-xs">
              (Last updated: {safeFormat(lastUpdated, 'HH:mm:ss')})
            </span>
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Main Stats Cards - Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <StatsCard 
          title="Total Users" 
          value={stats.totalUsers.toLocaleString()} 
          icon={UsersIcon} 
          color="text-blue-400"
          subValue={stats.newUsersToday}
          subLabel="New today"
          trend={5.2}
          trendLabel="vs last week"
        />
        <StatsCard 
          title="Total Cards" 
          value={stats.totalCards.toLocaleString()} 
          icon={CreditCardIcon} 
          color="text-green-400"
        />
        <StatsCard 
          title="Total Matches" 
          value={stats.totalMatches.toLocaleString()} 
          icon={CalendarIcon} 
          color="text-yellow-400"
          subValue={stats.matchesToday}
          subLabel="Today"
        />
        <StatsCard 
          title="Active Today" 
          value={stats.activeUsersToday} 
          icon={UserGroupIcon} 
          color="text-emerald-400"
        />
        <StatsCard 
          title="Revenue" 
          value={`$${stats.totalRevenue.toLocaleString()}`} 
          icon={CurrencyDollarIcon} 
          color="text-green-400"
        />
        <StatsCard 
          title="Support" 
          value={stats.totalSupportMessages} 
          icon={ChatBubbleLeftRightIcon} 
          color="text-red-400"
        />
      </div>

      {/* Mini Stats - Row 2 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <MiniStatCard 
          title="Premium Names" 
          value={stats.totalPremiumPurchases} 
          icon={StarIcon} 
          color="text-yellow-400"
        />
        <MiniStatCard 
          title="Pending Sponsorships" 
          value={stats.pendingSponsorships} 
          icon={TrophyIcon} 
          color="text-purple-400"
        />
        <MiniStatCard 
          title="Matches Today" 
          value={stats.matchesToday} 
          icon={FireIcon} 
          color="text-orange-400"
        />
        <MiniStatCard 
          title="Avg Cards/User" 
          value={stats.totalUsers > 0 ? Math.round(stats.totalCards / stats.totalUsers) : 0} 
          icon={CpuChipIcon} 
          color="text-cyan-400"
        />
        <MiniStatCard 
          title="Continents" 
          value={userContinentDistribution.filter(c => c.count > 0).length || 0} 
          icon={GlobeAltIcon} 
          color="text-indigo-400"
        />
        <MiniStatCard 
          title="Season" 
          value={seasonStats?.currentSeason || '—'} 
          icon={TrophyIcon} 
          color="text-yellow-400"
          subtitle={`${seasonStats?.totalMatches || 0} matches`}
        />
      </div>

      {/* Charts - Row 3 */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Match Trend */}
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <FireIcon className="h-5 w-5 text-orange-400" />
            Matches per Day (Last 7 days)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={matchTrend} onClick={handleChartClick}>
              <defs>
                <linearGradient id="matchGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FBBF24" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#FBBF24" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} />
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }} />
              <Area type="monotone" dataKey="count" stroke="#FBBF24" strokeWidth={2} fill="url(#matchGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Card Rarity Distribution */}
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <CreditCardIcon className="h-5 w-5 text-green-400" />
            Card Rarity Distribution
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie 
                data={cardRarityDistribution} 
                dataKey="count" 
                nameKey="rarity" 
                cx="50%" 
                cy="50%" 
                outerRadius={90}
                innerRadius={40}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
                onClick={handleChartClick}
              >
                {cardRarityDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts - Row 4 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User by Continent */}
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <GlobeAltIcon className="h-5 w-5 text-blue-400" />
            Users by Continent
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={userContinentDistribution} layout="vertical" onClick={handleChartClick}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
              <XAxis type="number" stroke="#9CA3AF" fontSize={11} />
              <YAxis type="category" dataKey="continent" stroke="#9CA3AF" fontSize={11} width={70} />
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }} />
              <Bar dataKey="count" fill="#60A5FA" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Competition Distribution */}
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrophyIcon className="h-5 w-5 text-yellow-400" />
            Matches by Competition
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie 
                data={competitionDistribution} 
                dataKey="count" 
                nameKey="competition" 
                cx="50%" 
                cy="50%" 
                outerRadius={80}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
                onClick={handleChartClick}
              >
                {competitionDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[(index + 3) % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Trend */}
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <CurrencyDollarIcon className="h-5 w-5 text-green-400" />
            Revenue (Last 7 days)
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={revenueData} onClick={handleChartClick}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} />
              <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }} />
              <Bar dataKey="amount" fill="#34D399" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Scorers & Top Players */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <BoltIcon className="h-5 w-5 text-yellow-400" />
            Top Scorers (All Time)
          </h3>
          <div className="space-y-2">
            {topScorers.slice(0, 5).map((player, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-700/30 rounded-lg px-4 py-2">
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm font-mono w-6">#{idx + 1}</span>
                  <span className="text-white font-medium">{player.username}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-yellow-400 font-bold">{player.goals}</span>
                  <span className="text-gray-500 text-xs">goals</span>
                </div>
              </div>
            ))}
            {topScorers.length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">No data available</div>
            )}
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-blue-400" />
            Top Players (By Rating)
          </h3>
          <div className="space-y-2">
            {topPlayers.slice(0, 5).map((player, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-700/30 rounded-lg px-4 py-2">
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm font-mono w-6">#{idx + 1}</span>
                  <span className="text-white font-medium">{player.username}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-green-400 font-bold">{player.rating}</span>
                  <span className="text-gray-500 text-xs">rating</span>
                </div>
              </div>
            ))}
            {topPlayers.length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">No data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Matches & Users */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-gray-400" />
            Recent Matches
          </h3>
          <RecentMatchesTable matches={recentMatches} />
        </div>

        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-blue-400" />
            Recent Users
          </h3>
          <div className="space-y-2">
            {recentUsers.slice(0, 10).map((user) => (
              <div key={user.id} className="flex items-center justify-between bg-gray-700/30 rounded-lg px-4 py-2">
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium">{user.username}</span>
                  <span className="text-xs text-gray-500">{user.continent}</span>
                </div>
                <div className="text-xs text-gray-400">
                  {safeFormatDistanceToNow(user.created_at)}
                </div>
              </div>
            ))}
            {recentUsers.length === 0 && (
              <div className="text-gray-500 text-sm text-center py-4">No users yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Premium Names Stats */}
      {premiumNameStats && (
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-yellow-400" />
            Premium Names Overview
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{premiumNameStats.totalNames}</p>
              <p className="text-xs text-gray-400">Total Names</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">{premiumNameStats.soldNames}</p>
              <p className="text-xs text-gray-400">Sold</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-400">{premiumNameStats.availableNames}</p>
              <p className="text-xs text-gray-400">Available</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-400">{premiumNameStats.pendingRequests}</p>
              <p className="text-xs text-gray-400">Pending</p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-gray-500 text-xs border-t border-gray-800 pt-4">
        <p>Last updated: {safeFormat(new Date(), 'PPPppp')}</p>
        <p className="mt-1">Data refreshes automatically every 5 minutes</p>
      </div>
    </div>
  );
};

export default Dashboard;